import { EventEmitter } from 'events';
import assign from 'object-assign';
import MarkerFactory from 'components/shipment/maps/MarkerFactory';
import fullName from 'utils/formatters/fullName';
import AdminAppDispatcher from '../dispatcher/AdminAppDispatcher';
import AdminConstants from '../constants/AdminConstants';
import mapDrivers from '../utils/transformers/DriverTransformer';
import _ from 'underscore';
import { DOCUMENTS_PER_PAGE } from '../utils/DriverWebAPIUtils';
const ActionTypes = AdminConstants.ActionTypes;
const CHANGE_EVENT = 'DRIVER_CHANGE';
const DRIVERS_CHANGE_EVENT = 'DRIVERS_CHANGE_EVENT';
const SELECTED_SHIPMENT_CHANGE_EVENT = 'SELECTED_SHIPMENT_CHANGE_EVENT';
let allDrivers = [];
let availableDrivers = [];
let nearbyDrivers = [];
let totalNearbyDrivers;
let driverMarkers = [];
let driverOptions = {};
let authorizedDrivers = [];
let waypoints = [];
let manifests = [];
let manifestsMeta = {
    total: 0,
    page: 1,
    per_page: DOCUMENTS_PER_PAGE,
    total_pages: 1,
};
let activeMarkerId = null;
let activeMarkerListOpen = false;
let selectedShipment = null;
let forceSelectedShipmentId = null;
const pushDriverAsOption = function (driver) {
    const onDuty = driver.available ? 'On-Duty' : 'Off-Duty';
    let label = fullName(driver) +
        ' ' +
        driver.username +
        ' - (' +
        driver.carrier.name +
        ') - ' +
        onDuty;
    if (driverOptions[label]) {
        let count = 1;
        do {
            count += 1;
            label = label + ' [' + count + ']';
            console.log('Found a dup: ' + label);
        } while (driverOptions[label]);
    }
    driverOptions[label] = driver;
};
const DriverStore = assign({}, EventEmitter.prototype, {
    addDriversChangeListener(callback) {
        this.on(DRIVERS_CHANGE_EVENT, callback);
    },
    removeDriversChangeListener(callback) {
        this.removeListener(DRIVERS_CHANGE_EVENT, callback);
    },
    emitDriversChange() {
        this.emit(DRIVERS_CHANGE_EVENT);
    },
    addSelectedShipmentChangeListener(callback) {
        this.on(SELECTED_SHIPMENT_CHANGE_EVENT, callback);
    },
    removeSelectedShipmentChangeListener(callback) {
        this.removeListener(SELECTED_SHIPMENT_CHANGE_EVENT, callback);
    },
    emitSelectedShipmentChange() {
        this.emit(SELECTED_SHIPMENT_CHANGE_EVENT);
    },
    emitChange: function () {
        this.emit(CHANGE_EVENT);
    },
    addChangeListener: function (callback) {
        this.on(CHANGE_EVENT, callback);
    },
    removeChangeListener: function (callback) {
        this.removeListener(CHANGE_EVENT, callback);
    },
    getAllDrivers: function () {
        return allDrivers;
    },
    getManifest: function () {
        return manifests;
    },
    getManifestsMeta: function () {
        return manifestsMeta;
    },
    getAvailableDrivers: function () {
        return availableDrivers;
    },
    getAuthorizedDrivers: function () {
        return authorizedDrivers;
    },
    getNearbyDrivers: function () {
        return { nearbyDrivers: nearbyDrivers, totalPages: totalNearbyDrivers };
    },
    getAuthorizedDriversAsOptions: function () {
        driverOptions = {};
        authorizedDrivers.map(pushDriverAsOption);
        return Object.keys(driverOptions);
    },
    getDriverForOption: function (label) {
        return driverOptions[label];
    },
    getDriver: function (id) {
        return allDrivers.find((d) => d._id === id);
    },
    getDriverWaypoints: function () {
        return waypoints;
    },
    setCarrierData(drivers = []) {
        driverMarkers = drivers.map((driver) => {
            const newMarker = MarkerFactory.createDriverMarker(driver);
            return this.initializeNewMarker(newMarker, driver);
        });
    },
    updateSelectedShipment(shipments = []) {
        const forceSelectedShipment = shipments.find((e) => e.id === forceSelectedShipmentId);
        if (forceSelectedShipment) {
            selectedShipment = forceSelectedShipment;
            forceSelectedShipmentId = null;
            this.emitSelectedShipmentChange();
        }
    },
    initializeNewMarker(marker, driver) {
        const { _id, first_name, last_name, fullName: driverFullName, phone, equipmentClassLabel, available, has_active_jobs, } = driver;
        marker.listOpen = false;
        marker.jobs = driver.jobs;
        marker.driver = {
            _id,
            first_name,
            last_name,
            fullName: driverFullName,
            equipmentClassLabel,
            phone,
        };
        marker.isActive = this.isActiveMarker(marker.id);
        marker.hasActiveJobs = has_active_jobs;
        if (marker.isActive && activeMarkerListOpen) {
            marker.listOpen = true;
        }
        return Object.assign(Object.assign({}, marker), { available,
            equipmentClassLabel });
    },
    isActiveMarker(id) {
        return id === activeMarkerId;
    },
    getDriverMarkers() {
        return driverMarkers;
    },
    getActiveMarker() {
        return driverMarkers.find((marker) => marker.id === activeMarkerId);
    },
    setActiveDriver(id) {
        const newActiveMarker = driverMarkers.find((marker) => marker.id === id);
        newActiveMarker.isActive = true;
        newActiveMarker.listOpen = true;
        activeMarkerId = id;
        activeMarkerListOpen = true;
    },
    closeInfoWindow(driverId) {
        if (driverId) {
            const marker = driverMarkers.find((e) => e.id === driverId);
            marker.listOpen = false;
            activeMarkerListOpen = false;
        }
        else if (activeMarkerId) {
            const marker = driverMarkers.find((e) => e.id === activeMarkerId);
            marker.listOpen = false;
            activeMarkerListOpen = false;
        }
    },
    updateActiveDriver(id) {
        const activeMarker = this.getActiveMarker();
        if (activeMarker && !this.isActiveMarker(id)) {
            activeMarker.isActive = false;
            activeMarker.listOpen = false;
        }
        this.setActiveDriver(id);
    },
    setSelectedShipment(shipment) {
        selectedShipment = shipment;
    },
    getSelectedShipment() {
        return selectedShipment;
    },
    resetSelectedShipment() {
        selectedShipment = null;
    },
    setForceSelectedShipmentId(shipmentId) {
        forceSelectedShipmentId = shipmentId;
    },
});
function updateDriverField(data, field) {
    const { driver_id: driverId, status } = data;
    const index = allDrivers.findIndex((d) => d._id === driverId);
    if (index !== -1) {
        allDrivers[index][field] = status;
        DriverStore.emitChange();
    }
}
function updateDriver(driver) {
    const index = allDrivers.findIndex((d) => d._id === driver._id);
    if (index === -1) {
        allDrivers = [driver];
    }
    else {
        allDrivers = [
            ...allDrivers.slice(0, index),
            driver,
            ...allDrivers.slice(index + 1),
        ];
    }
    DriverStore.emitChange();
}
DriverStore.dispatchToken = AdminAppDispatcher.register(async function (payload) {
    const action = payload.action;
    switch (action.type) {
        case ActionTypes.RECEIVE_AVAILABLE_DRIVERS:
            availableDrivers = await mapDrivers(action.drivers || []);
            DriverStore.emitChange();
            break;
        case ActionTypes.RECEIVE_AUTHORIZED_DRIVERS:
            authorizedDrivers = await mapDrivers(action.drivers || []);
            availableDrivers = authorizedDrivers.filter((d) => d.available);
            DriverStore.setCarrierData(authorizedDrivers);
            DriverStore.emitChange();
            break;
        case ActionTypes.RECEIVE_NEARBY_DRIVERS:
            nearbyDrivers = await mapDrivers(action.drivers || []);
            totalNearbyDrivers = action.count;
            DriverStore.emitChange();
            break;
        case ActionTypes.RECEIVE_DRIVERS:
            allDrivers = await mapDrivers(action.drivers || []);
            DriverStore.emitChange();
            break;
        case ActionTypes.RECEIVE_DRIVER_MANIFEST:
            // Handling Get Manifests
            if (action.data) {
                // Handle paginated response structure
                if (action.data.data && action.data.meta) {
                    manifests = action.data.data;
                    manifestsMeta = action.data.meta;
                }
                else {
                    // Fallback for non-paginated response
                    manifests = action.data;
                    manifestsMeta = {
                        total: action.data.length,
                        page: 1,
                        per_page: action.data.length,
                        total_pages: 1,
                    };
                }
                DriverStore.emitChange();
            }
            break;
        case ActionTypes.RECEIVE_DEPARTED_MANIFEST:
            // Handling update after depart manifests clicked
            if (manifests.length && action.data) {
                const updatedManifests = manifests.map((m) => {
                    if (m._id === action.data._id) {
                        return action.data;
                    }
                    else {
                        return m;
                    }
                });
                manifests = updatedManifests;
                DriverStore.emitChange();
            }
            break;
        case ActionTypes.TOGGLE_DRIVER_AUTHORIZATION_SUCCESS:
            updateDriverField(action.data, 'authorized');
            break;
        case ActionTypes.TOGGLE_RECEIVE_OFFERS_SUCCESS:
            updateDriverField(action.data, 'receive_offers');
            break;
        case ActionTypes.TOGGLE_MULTIPLE_PICKUP_SUCCESS:
            updateDriverField(action.data, 'can_accept_offers_before_pickup');
            break;
        case ActionTypes.RECEIVE_DRIVER:
            updateDriver(action.driver);
            break;
        case ActionTypes.RECEIVE_DRIVER_WAYPOINTS:
            waypoints = action.waypoints;
            DriverStore.emitChange();
            break;
        default:
        // do nothing
    }
});
export default DriverStore;
